import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Input,
    IconButton,
    Chip,
    ClickAwayListener,
    Typography,
} from '@mui/material';
import search from '../../../../public/assets/icons/search.svg';
import cancel from '../../../../public/assets/icons/cancel.svg';

interface SearchOption {
    key: string;
    label: string;
}

interface SmartSearchFieldProps {
    searchOptions: SearchOption[];
    defaultOption: string;
    onSearch: (searchType: string, searchValue: string) => void;
    onClear: () => void;
    placeholder?: string;
    value?: string;
    width?: string;
}

export const SmartSearchField: React.FC<SmartSearchFieldProps> = ({
                                                                      searchOptions,
                                                                      defaultOption,
                                                                      onSearch,
                                                                      onClear,
                                                                      placeholder = "Search by patient MRN",
                                                                      value = "",
                                                                      width = "14vw"
                                                                  }) => {
    const [inputValue, setInputValue] = useState(value);
    const [selectedToken, setSelectedToken] = useState<SearchOption | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<SearchOption[]>(searchOptions);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let filtered = searchOptions;

        // Exclude the currently selected token from suggestions
        if (selectedToken) {
            filtered = searchOptions.filter(option => option.key !== selectedToken.key);
        }

        setFilteredSuggestions(filtered);
    }, [searchOptions, selectedToken]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredSuggestions]);

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    const handleFocus = () => {
        if (!selectedToken && !inputValue) {
            const defaultTokenOption = searchOptions.find(opt => opt.key === defaultOption);
            if (defaultTokenOption) {
                setSelectedToken(defaultTokenOption);
            }
        }
        setShowSuggestions(true);
    };

    const handleBlur = () => {
        // Longer delay to prevent flickering when clearing and refocusing
        setTimeout(() => {
            if (!containerRef.current?.contains(document.activeElement)) {
                setShowSuggestions(false);
            }
        }, 200);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowSuggestions(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && showSuggestions && filteredSuggestions.length > 0) {
                selectSuggestion(filteredSuggestions[highlightedIndex]);
            } else if (selectedToken && inputValue.trim()) {
                handleSearch();
                setShowSuggestions(false);
                setHighlightedIndex(-1);
            }
        } else if (e.key === ' ' || e.key === 'Spacebar') {
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            if (showSuggestions && filteredSuggestions.length > 0) {
                const newIndex = highlightedIndex < filteredSuggestions.length - 1 ? highlightedIndex + 1 : -1;
                setHighlightedIndex(newIndex);
            }
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            if (showSuggestions && filteredSuggestions.length > 0) {
                const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : (highlightedIndex === 0 ? -1 : filteredSuggestions.length - 1);
                setHighlightedIndex(newIndex);
            }
        } else if (e.key === 'Tab' && showSuggestions && filteredSuggestions.length > 0 && highlightedIndex >= 0) {
            e.preventDefault();
            selectSuggestion(filteredSuggestions[highlightedIndex]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        } else if (e.key === 'Backspace' && inputValue === '' && selectedToken) {
            // Do nothing - tokens cannot be deleted with Backspace
        }
    };

    const selectSuggestion = (option: SearchOption) => {
        setSelectedToken(option);
        setInputValue('');
        setHighlightedIndex(-1);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 10);
    };

    const handleSearch = () => {
        if (selectedToken && inputValue.trim()) {
            onSearch(selectedToken.key, inputValue.trim());
        }
    };

    const handleClear = () => {
        setInputValue('');
        setSelectedToken(null);
        setShowSuggestions(false);
        onClear();
        setTimeout(() => {
            inputRef.current?.focus();
        }, 10);
    };

    const handleClickAway = () => {
        if (document.activeElement !== inputRef.current) {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    };

    const highlightMatchingText = (text: string, query: string) => {
        if (!query) return text;

        // Escape regex special characters to prevent ReDoS attacks
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        fontWeight: 'bold',
                        color: '#203d48',
                        display: 'inline-block',
                        width: 'auto',
                        textAlign: 'center'
                    }}
                >
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box ref={containerRef} sx={{ position: 'relative', width }}>
                <Input
                    ref={inputRef}
                    sx={{
                        width: "100%",
                        paddingY: '8px',
                        '&:before': {
                            borderBottom: '1px solid #a9d5e5',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '2px solid #a9d5e5',
                        },
                        '&:after': {
                            borderBottom: '2px solid #a9d5e5',
                        }
                    }}
                    value={inputValue}
                    placeholder={selectedToken ? '' : placeholder}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyPress}
                    onClick={handleContainerClick}
                    startAdornment={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '16px' }}>
                            {/* Search icon */}
                            <IconButton
                                disableRipple={true}
                                sx={{ padding: 0, minWidth: 'auto' }}
                            >
                                <img src={search} alt="search" style={{ height: "18px", width: "18px" }} />
                            </IconButton>

                            {/* Selected token chip - using chips-teal-50 background */}
                            {selectedToken && (
                                <Chip
                                    data-testid="selected-token"
                                    label={selectedToken.label}
                                    size="small"
                                    sx={{
                                        height: '26px',
                                        backgroundColor: '#edf4f7',
                                        border: '1px solid #a9d5e5',
                                        color: '#203d48',
                                        borderRadius: '4px',
                                        marginRight: '12px',
                                        '& .MuiChip-label': {
                                            fontSize: '13px',
                                            fontWeight: 400,
                                            paddingLeft: '10px',
                                            paddingRight: '10px'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    }
                    endAdornment={
                        (inputValue.length > 0 || selectedToken) && (
                            <IconButton
                                onClick={handleClear}
                                disableRipple={true}
                                sx={{
                                    padding: 0,
                                    minWidth: 'auto',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #a9d5e5',
                                    '&:hover': {
                                        backgroundColor: '#EEEEEE'
                                    }
                                }}
                            >
                                <img src={cancel} alt="cancel" style={{ height: "12px", width: "12px", opacity: 0.7 }} />
                            </IconButton>
                        )
                    }
                />

                {/* Suggestions dropdown */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                    <Box
                        data-testid="suggestions-dropdown"
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: 'white',
                            border: '1px solid #a9d5e5',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        <Box
                            sx={{
                                padding: '12px 10px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                width: '100%'
                            }}
                        >
                            {filteredSuggestions.map((option, index) => {
                                const isHighlighted = index === highlightedIndex && highlightedIndex >= 0;

                                return (
                                    <Box
                                        key={option.key}
                                        data-testid="suggestion-option"
                                        onClick={() => selectSuggestion(option)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        sx={{
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            border: isHighlighted ? '2px solid #a9d5e5' : '1px solid #E0E0E0',
                                            backgroundColor: isHighlighted ? '#edf4f7' : 'white',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease',
                                            flex: '0 0 auto',
                                            minWidth: 'fit-content',
                                            '&:hover': {
                                                backgroundColor: '#edf4f7'
                                            }
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                color: '#203d48',
                                                fontWeight: 400,
                                                whiteSpace: 'nowrap',
                                                fontVariantNumeric: 'tabular-nums',
                                                letterSpacing: '0.02em'
                                            }}
                                        >
                                            {highlightMatchingText(option.label, inputValue)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default SmartSearchField;